import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { RoomEvent, ParticipantEvent, type TrackPublication } from 'livekit-client';
import { useCall, Track, type RemoteParticipant } from '@/contexts/call-context';
import { GlobalCallAudioSink } from '@/components/calls/GlobalCallAudioSink';
import { formatUserDisplayName } from '@shared/display';
import { Button } from '@/components/ui/button';
import {
  Mic,
  MicOff,
  Video,
  VideoOff,
  Monitor,
  MonitorOff,
  PhoneOff,
  X,
  Maximize2,
  Minimize2,
  PictureInPicture2,
  Loader2,
  Activity,
  Folder,
  FolderX,
  FolderKanban,
  ChevronDown,
  ChevronUp,
  Search,
  History,
  Captions,
  CaptionsOff,
  Pause,
  Play,
  Camera,
  Volume2,
  VolumeX,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { resolveCallWindowPresentation } from '@/lib/call-window-presentation';
import { useAuth } from '@/contexts/auth-context';
import { useQuery } from '@tanstack/react-query';
import { useMediaDevices } from '@/hooks/use-media-devices';
import { useNow } from '@/components/timeline/useNow';
import { useToast } from '@/hooks/use-toast';
import { DeviceMenu } from '@/components/calls/DeviceMenu';
import { CallConnectionStats } from '@/components/calls/CallConnectionStats';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import * as AlertDialogPrimitive from '@radix-ui/react-alert-dialog';
import { Input } from '@/components/ui/input';
import { getUserColor } from '@/lib/user-color';

interface RecentProjectLite {
  id: string;
  projectName: string;
  clientName: string | null;
}

interface UserLite {
  id: number;
  firstName?: string;
  lastName?: string;
  shortCode?: string;
  username?: string;
  colorCode?: string;
}

/**
 * Format a past timestamp as a short relative-time hint, e.g.
 * "just now", "5 min ago", "2 h ago", "yesterday", "3 days ago",
 * "2 wk ago". Used by the in-call project picker to indicate when each
 * "Last transcribed" project was last used.
 */
function formatRelativeEn(timestamp: string | null | undefined, nowMs: number): string | null {
  if (!timestamp) return null;
  const then = Date.parse(timestamp);
  if (!Number.isFinite(then)) return null;
  const diffMs = Math.max(0, nowMs - then);
  const minute = 60_000;
  const hour = 60 * minute;
  const day = 24 * hour;
  if (diffMs < minute) return 'just now';
  if (diffMs < hour) return `${Math.floor(diffMs / minute)} min ago`;
  if (diffMs < day) return `${Math.floor(diffMs / hour)} h ago`;
  if (diffMs < 2 * day) return 'yesterday';
  if (diffMs < 7 * day) return `${Math.floor(diffMs / day)} days ago`;
  if (diffMs < 30 * day) return `${Math.floor(diffMs / (7 * day))} wk ago`;
  if (diffMs < 365 * day) return `${Math.floor(diffMs / (30 * day))} mo ago`;
  return `${Math.floor(diffMs / (365 * day))} yr ago`;
}

/** Pull the integer user id out of a LiveKit identity like "u7-xxxx". */
function userIdFromIdentity(identity: string | undefined | null): number | null {
  if (!identity) return null;
  const m = identity.match(/^u(\d+)-/);
  return m ? Number(m[1]) : null;
}

/** Build a sensible fallback label for a LiveKit identity when the user isn't found. */
function identityFallbackLabel(identity: string | undefined | null): string {
  if (!identity) return 'Participant';
  const id = userIdFromIdentity(identity);
  return id != null ? `User ${id}` : identity;
}

/**
 * Resolve a participant's display name with a defence-in-depth fallback chain:
 *   1. Local users cache (formatted via the shared formatter)
 *   2. The display name LiveKit baked into the participant token (server side)
 *   3. The provided final fallback (e.g. "User 5" or a custom placeholder)
 *
 * Tier 2 closes a brief race window where the initiator joins their own room
 * before `/api/users` has resolved on the client; the server-minted name
 * carries the truth so we never fall back to "User N" while the user record
 * exists.
 */
function resolveDisplayName(
  user: UserLite | undefined,
  participant: { name?: string; identity?: string } | null | undefined,
  finalFallback: string,
): string {
  const livekitName = participant?.name?.trim();
  return formatUserDisplayName(user, livekitName || finalFallback);
}

/** Single character used inside the colour avatar bubble. */
function avatarInitial(user: UserLite | undefined): string {
  return (user?.firstName?.[0] || user?.shortCode?.[0] || '?').toUpperCase();
}

function avatarColor(user: UserLite | undefined): string {
  return getUserColor(user?.colorCode);
}

function Avatar({ user, size = 'md' }: { user: UserLite | undefined; size?: 'xs' | 'sm' | 'md' | 'lg' }) {
  const cls =
    size === 'lg'
      ? 'h-16 w-16 text-2xl'
      : size === 'sm'
        ? 'h-6 w-6 text-[10px]'
        : size === 'xs'
          ? 'h-4 w-4 text-[8px]'
          : 'h-9 w-9 text-sm';
  return (
    <div
      className={cn('rounded-full flex items-center justify-center text-white font-semibold shrink-0', cls)}
      style={{ backgroundColor: avatarColor(user) }}
    >
      {avatarInitial(user)}
    </div>
  );
}

/** Subscribe to a remote participant's speaking state. */
function useIsSpeaking(participant: RemoteParticipant | null): boolean {
  const [speaking, setSpeaking] = useState(participant?.isSpeaking ?? false);
  useEffect(() => {
    if (!participant) return;
    setSpeaking(participant.isSpeaking);
    const handler = (s: boolean) => setSpeaking(s);
    participant.on(ParticipantEvent.IsSpeakingChanged, handler);
    return () => {
      participant.off(ParticipantEvent.IsSpeakingChanged, handler);
    };
  }, [participant]);
  return speaking;
}

/** Subtle "Reconnecting…" overlay shown on top of a participant tile while
 * the server-side grace timer is running. Pointer-events are disabled so it
 * never blocks the underlying tile's interactions. */
function ReconnectingOverlay() {
  return (
    <div
      className="absolute inset-0 flex items-center justify-center bg-black/40 pointer-events-none"
      data-testid="reconnecting-overlay"
    >
      <span className="flex items-center gap-1.5 text-[11px] font-medium text-white bg-black/70 px-2 py-1 rounded">
        <Loader2 className="h-3 w-3 animate-spin" />
        Reconnecting…
      </span>
    </div>
  );
}

/** Ghost tile for a user whose LiveKit participant has gone away but who is
 * still inside the server's reconnect grace window. Mirrors the dimensions of
 * a real ParticipantTile so the grid doesn't reflow when the participant
 * actually drops or comes back. */
function ReconnectingPlaceholderTile({
  user,
  fallbackLabel,
  compact = false,
}: {
  user: UserLite | undefined;
  fallbackLabel: string;
  compact?: boolean;
}) {
  // No live LiveKit participant here — they've already disconnected — so the
  // fallback chain skips tier 2 (participant.name) and goes straight to the
  // caller-provided label.
  const name = resolveDisplayName(user, null, fallbackLabel);
  return (
    <div
      className={cn(
        'relative bg-black rounded-lg overflow-hidden border border-border',
        compact ? 'aspect-video w-32 shrink-0' : 'aspect-video min-h-[120px]',
      )}
      data-testid={`reconnecting-tile-${user?.id ?? fallbackLabel}`}
    >
      <div className="absolute inset-0 flex items-center justify-center bg-muted">
        <Avatar user={user} size={compact ? 'md' : 'lg'} />
      </div>
      <ReconnectingOverlay />
      <div className="absolute bottom-1 left-1 right-1 flex items-center gap-1 text-[11px] text-white">
        <span className="bg-black/55 px-1.5 py-0.5 rounded flex items-center gap-1 max-w-full truncate">
          <span
            className="inline-block h-2 w-2 rounded-full shrink-0"
            style={{ backgroundColor: avatarColor(user) }}
          />
          <span className="truncate">{name}</span>
        </span>
      </div>
    </div>
  );
}

/** Tile rendering a remote participant's camera + audio. */
function ParticipantTile({
  participant,
  user,
  compact = false,
  reconnecting = false,
}: {
  participant: RemoteParticipant;
  user: UserLite | undefined;
  compact?: boolean;
  reconnecting?: boolean;
}) {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const [hasVideo, setHasVideo] = useState(false);
  const speaking = useIsSpeaking(participant);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const attach = () => {
      let videoAttached = false;
      participant.trackPublications.forEach((pub) => {
        if (!pub.track) return;
        // Attach only the camera here — screen share is rendered by
        // ScreenShareStage and ALL audio (mic + screen-share audio) is owned
        // by GlobalCallAudioSink which lives outside any portal subtree.
        if (pub.kind === Track.Kind.Video && pub.source !== Track.Source.ScreenShare) {
          pub.track.attach(video);
          videoAttached = true;
        }
      });
      setHasVideo(videoAttached);
    };
    attach();
    const handler = () => attach();
    participant.on('trackSubscribed', handler);
    participant.on('trackUnsubscribed', handler);
    participant.on('trackPublished', handler);
    participant.on('trackUnpublished', handler);
    participant.on('trackMuted', handler);
    participant.on('trackUnmuted', handler);
    return () => {
      participant.off('trackSubscribed', handler);
      participant.off('trackUnsubscribed', handler);
      participant.off('trackPublished', handler);
      participant.off('trackUnpublished', handler);
      participant.off('trackMuted', handler);
      participant.off('trackUnmuted', handler);
      // Scope detach to VIDEO tracks only — calling detach() on audio tracks
      // would rip them out of the GlobalCallAudioSink and silence the call
      // every time this tile remounts (e.g. on popout toggle).
      try {
        participant.trackPublications.forEach((pub) => {
          if (pub.kind === Track.Kind.Video) pub.track?.detach();
        });
      } catch {}
    };
  }, [participant]);

  const name = resolveDisplayName(user, participant, identityFallbackLabel(participant.identity));

  return (
    <div
      className={cn(
        'relative bg-black rounded-lg overflow-hidden border transition-shadow',
        speaking ? 'border-green-500 shadow-[0_0_0_2px_rgba(34,197,94,0.6)]' : 'border-border',
        compact ? 'aspect-video w-32 shrink-0' : 'aspect-video min-h-[120px]',
      )}
      data-testid={`participant-tile-${participant.identity}`}
    >
      <video ref={videoRef} autoPlay playsInline className={cn('w-full h-full object-cover', !hasVideo && 'hidden')} />
      {!hasVideo && (
        <div className="absolute inset-0 flex items-center justify-center bg-muted">
          <Avatar user={user} size={compact ? 'md' : 'lg'} />
        </div>
      )}
      {reconnecting && <ReconnectingOverlay />}
      <div className="absolute bottom-1 left-1 right-1 flex items-center gap-1 text-[11px] text-white">
        <span className="bg-black/55 px-1.5 py-0.5 rounded flex items-center gap-1 max-w-full truncate">
          <span
            className="inline-block h-2 w-2 rounded-full shrink-0"
            style={{ backgroundColor: avatarColor(user) }}
          />
          <span className="truncate">{name}</span>
        </span>
      </div>
    </div>
  );
}

/** Local self-preview camera tile. */
function LocalVideoPreview({ user, compact = false }: { user: UserLite | undefined; compact?: boolean }) {
  const { activeCall } = useCall();
  const ref = useRef<HTMLVideoElement | null>(null);
  const [hasVideo, setHasVideo] = useState(false);

  useEffect(() => {
    if (!activeCall) return;
    const lp = activeCall.room.localParticipant;
    const attach = () => {
      const cam = lp.getTrackPublication(Track.Source.Camera)?.track;
      if (cam && ref.current) {
        cam.attach(ref.current);
        setHasVideo(true);
      } else {
        setHasVideo(false);
      }
    };
    attach();
    lp.on('trackPublished', attach);
    lp.on('trackUnpublished', attach);
    lp.on('trackMuted', attach);
    lp.on('trackUnmuted', attach);
    return () => {
      lp.off('trackPublished', attach);
      lp.off('trackUnpublished', attach);
      lp.off('trackMuted', attach);
      lp.off('trackUnmuted', attach);
      try { lp.getTrackPublication(Track.Source.Camera)?.track?.detach(); } catch {}
    };
  }, [activeCall]);

  const name = `${formatUserDisplayName(user, 'You')} (You)`;

  return (
    <div className={cn('relative bg-black rounded-lg overflow-hidden border border-border', compact ? 'aspect-video w-32 shrink-0' : 'aspect-video min-h-[120px]')}>
      <video ref={ref} autoPlay playsInline muted className={cn('w-full h-full object-cover', !hasVideo && 'hidden')} />
      {!hasVideo && (
        <div className="absolute inset-0 flex items-center justify-center bg-muted">
          <Avatar user={user} size={compact ? 'md' : 'lg'} />
        </div>
      )}
      <div className="absolute bottom-1 left-1 right-1 flex items-center gap-1 text-[11px] text-white">
        <span className="bg-black/55 px-1.5 py-0.5 rounded flex items-center gap-1 max-w-full truncate">
          <span
            className="inline-block h-2 w-2 rounded-full shrink-0"
            style={{ backgroundColor: avatarColor(user) }}
          />
          <span className="truncate">{name}</span>
        </span>
      </div>
    </div>
  );
}

/**
 * Cross-browser Fullscreen API helpers. We need to operate on the OWNING
 * document of the stage element because the call panel can be portalled into
 * a Document Picture-in-Picture window whose `document` is distinct from the
 * main app's. All promise rejections are swallowed — callers (mount/unmount
 * cleanup, click handlers) shouldn't have to defend against teardown-time
 * failures from a forcibly-closed PiP doc.
 */
type FsDocument = Document & {
  webkitFullscreenElement?: Element | null;
  webkitExitFullscreen?: () => Promise<void> | void;
};
type FsElement = HTMLElement & {
  webkitRequestFullscreen?: () => Promise<void> | void;
};
function requestStageFullscreen(el: HTMLElement | null) {
  if (!el) return;
  const e = el as FsElement;
  try {
    const p = e.requestFullscreen ? e.requestFullscreen() : e.webkitRequestFullscreen?.();
    if (p && typeof (p as Promise<void>).catch === 'function') (p as Promise<void>).catch(() => {});
  } catch {}
}
function exitStageFullscreen(doc: Document | null | undefined) {
  if (!doc) return;
  const d = doc as FsDocument;
  try {
    const p = d.exitFullscreen ? d.exitFullscreen() : d.webkitExitFullscreen?.();
    if (p && typeof (p as Promise<void>).catch === 'function') (p as Promise<void>).catch(() => {});
  } catch {}
}
function getStageFullscreenElement(doc: Document | null | undefined): Element | null {
  if (!doc) return null;
  const d = doc as FsDocument;
  return d.fullscreenElement ?? d.webkitFullscreenElement ?? null;
}
function subscribeFullscreenChange(doc: Document | null | undefined, handler: () => void): () => void {
  if (!doc) return () => {};
  doc.addEventListener('fullscreenchange', handler);
  doc.addEventListener('webkitfullscreenchange', handler as EventListener);
  return () => {
    doc.removeEventListener('fullscreenchange', handler);
    doc.removeEventListener('webkitfullscreenchange', handler as EventListener);
  };
}

/** Large screen-share renderer. Accepts either a remote participant or the local one. */
function ScreenShareStage({
  remote,
  remoteUser,
  local,
  localUser,
  screenShareAudioMuted,
  onToggleScreenShareAudioMute,
}: {
  remote: RemoteParticipant | null;
  remoteUser: UserLite | undefined;
  local: { isLocal: true } | null;
  localUser: UserLite | undefined;
  screenShareAudioMuted: boolean;
  onToggleScreenShareAudioMute: () => void;
}) {
  const { activeCall } = useCall();
  const ref = useRef<HTMLVideoElement | null>(null);
  const stageRef = useRef<HTMLDivElement | null>(null);
  // Tracks whether the current remote sharer is publishing a ScreenShareAudio
  // track. The mute toggle is only useful (and only rendered) when there is
  // actually screen-share audio to silence.
  const [hasShareAudio, setHasShareAudio] = useState(false);
  const [isStageFullscreen, setIsStageFullscreen] = useState(false);

  // Watch fullscreen changes on the OWNING document so Escape works in both
  // the main document and the Document Picture-in-Picture popout. Browsers
  // only allow one fullscreen element per document at a time, so entering
  // stage-fullscreen while panel-fullscreen is active will auto-replace it.
  useEffect(() => {
    const el = stageRef.current;
    const doc = el?.ownerDocument;
    if (!doc) return;
    const handler = () => {
      setIsStageFullscreen(getStageFullscreenElement(doc) === stageRef.current);
    };
    handler();
    const unsubscribe = subscribeFullscreenChange(doc, handler);
    return () => {
      unsubscribe();
      // If we're unmounting while still fullscreen (e.g. sharer stopped
      // sharing), exit so the user isn't left staring at a black rectangle.
      if (getStageFullscreenElement(doc) === el) exitStageFullscreen(doc);
    };
  }, []);

  const toggleStageFullscreen = useCallback(() => {
    const el = stageRef.current;
    if (!el) return;
    const doc = el.ownerDocument;
    if (getStageFullscreenElement(doc) === el) {
      exitStageFullscreen(doc);
    } else {
      requestStageFullscreen(el);
    }
  }, []);

  useEffect(() => {
    const v = ref.current;
    if (!v) return;
    let cleanup: (() => void) | undefined;

    if (remote) {
      const refreshShareAudio = () => {
        const pubs = Array.from(remote.trackPublications.values());
        const hasAudio = pubs.some(
          (p: TrackPublication) =>
            p.source === Track.Source.ScreenShareAudio && !!p.track && !p.isMuted,
        );
        setHasShareAudio(hasAudio);
      };
      const attach = () => {
        const pubs = Array.from(remote.trackPublications.values());
        const videoPub = pubs.find((p: TrackPublication) => p.source === Track.Source.ScreenShare);
        if (videoPub?.track) videoPub.track.attach(v);
        // Screen-share AUDIO is owned by GlobalCallAudioSink, not this stage.
        refreshShareAudio();
      };
      attach();
      const handler = () => attach();
      remote.on('trackSubscribed', handler);
      remote.on('trackUnsubscribed', handler);
      remote.on('trackMuted', handler);
      remote.on('trackUnmuted', handler);
      cleanup = () => {
        remote.off('trackSubscribed', handler);
        remote.off('trackUnsubscribed', handler);
        remote.off('trackMuted', handler);
        remote.off('trackUnmuted', handler);
        setHasShareAudio(false);
      };
    } else if (local && activeCall) {
      setHasShareAudio(false);
      const lp = activeCall.room.localParticipant;
      const attach = () => {
        const pub = lp.getTrackPublication(Track.Source.ScreenShare);
        if (pub?.track) pub.track.attach(v);
      };
      attach();
      lp.on('trackPublished', attach);
      lp.on('trackUnpublished', attach);
      cleanup = () => {
        lp.off('trackPublished', attach);
        lp.off('trackUnpublished', attach);
      };
    }

    return () => { cleanup?.(); try { v.srcObject = null; } catch {} };
  }, [remote, local, activeCall]);

  const sharerName = remote
    ? resolveDisplayName(remoteUser, remote, identityFallbackLabel(remote.identity))
    : 'You';
  const label = remote ? `${sharerName}'s screen` : 'Your screen';

  return (
    <div
      ref={stageRef}
      onDoubleClick={toggleStageFullscreen}
      title="Double-click to enlarge (Esc to exit)"
      className={cn(
        'relative bg-black rounded-lg overflow-hidden border border-border w-full h-full flex items-center justify-center',
        isStageFullscreen ? 'cursor-zoom-out' : 'cursor-zoom-in',
      )}
      data-testid="screen-share-stage"
    >
      <video ref={ref} autoPlay playsInline className="w-full h-full object-contain" />
      <div className="absolute top-2 left-2 text-[11px] text-white bg-black/60 px-2 py-1 rounded flex items-center gap-1.5">
        <Monitor className="h-3 w-3" />
        <Avatar user={remote ? remoteUser : localUser} size="xs" />
        <span>{label}</span>
      </div>
      {/* Click alternative to the dblclick gesture (helpful on trackpads with
          dblclick disabled). stopPropagation prevents a fast double click on
          the button from also firing the wrapper's onDoubleClick and
          re-toggling. */}
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); toggleStageFullscreen(); }}
        onDoubleClick={(e) => e.stopPropagation()}
        className="absolute bottom-2 right-2 inline-flex items-center justify-center text-white bg-black/60 hover:bg-black/75 p-1.5 rounded"
        aria-label={isStageFullscreen ? 'Exit fullscreen' : 'Enlarge shared screen'}
        title={isStageFullscreen ? 'Exit fullscreen (Esc)' : 'Enlarge shared screen'}
        data-testid="button-toggle-screen-share-fullscreen"
      >
        {isStageFullscreen ? <Minimize2 className="h-3.5 w-3.5" /> : <Maximize2 className="h-3.5 w-3.5" />}
      </button>
      {/* Listener-side mute for the presenter's tab/system audio. Only shown
          for REMOTE shares that are actually publishing ScreenShareAudio —
          local publishers don't see it (we never play back our own system
          audio) and we don't render a useless toggle when there's nothing to
          mute. Silences ONLY the ScreenShareAudio element in
          GlobalCallAudioSink, never the presenter's microphone. */}
      {remote && hasShareAudio && (
        <button
          type="button"
          onClick={onToggleScreenShareAudioMute}
          className="absolute top-2 right-2 inline-flex items-center gap-1 text-[11px] text-white bg-black/60 hover:bg-black/75 px-2 py-1 rounded"
          aria-pressed={screenShareAudioMuted}
          aria-label={screenShareAudioMuted ? 'Unmute shared screen audio' : 'Mute shared screen audio'}
          title={screenShareAudioMuted ? 'Unmute shared screen audio' : 'Mute shared screen audio'}
          data-testid="button-toggle-screen-share-audio-mute"
        >
          {screenShareAudioMuted ? <VolumeX className="h-3 w-3" /> : <Volume2 className="h-3 w-3" />}
          <span>{screenShareAudioMuted ? 'Audio off' : 'Audio on'}</span>
        </button>
      )}
    </div>
  );
}

/** Audio-only row showing who's in the call with a speaking indicator. */
function AudioParticipantRow({
  participant,
  user,
  reconnecting = false,
}: {
  participant: RemoteParticipant;
  user: UserLite | undefined;
  reconnecting?: boolean;
}) {
  const speaking = useIsSpeaking(participant);
  const name = resolveDisplayName(user, participant, identityFallbackLabel(participant.identity));
  return (
    <div
      className={cn(
        'flex items-center gap-2 px-2 py-1 rounded-md border transition-colors',
        speaking ? 'border-green-500 bg-green-500/10' : 'border-transparent',
        reconnecting && 'opacity-70',
      )}
      data-testid={`audio-participant-${participant.identity}`}
    >
      <Avatar user={user} size="sm" />
      <span className="text-xs truncate">{name}</span>
      {reconnecting ? (
        <span className="ml-auto flex items-center gap-1 text-[10px] text-muted-foreground">
          <Loader2 className="h-3 w-3 animate-spin" />
          Reconnecting…
        </span>
      ) : (
        speaking && <Mic className="h-3 w-3 text-green-500 ml-auto" />
      )}
    </div>
  );
}

/** Audio-only row counterpart to ReconnectingPlaceholderTile. */
function AudioReconnectingPlaceholderRow({
  user,
  fallbackLabel,
}: {
  user: UserLite | undefined;
  fallbackLabel: string;
}) {
  // Same shape as ReconnectingPlaceholderTile: no live participant available.
  const name = resolveDisplayName(user, null, fallbackLabel);
  return (
    <div
      className="flex items-center gap-2 px-2 py-1 rounded-md border border-transparent opacity-70"
      data-testid={`audio-reconnecting-${user?.id ?? fallbackLabel}`}
    >
      <Avatar user={user} size="sm" />
      <span className="text-xs truncate">{name}</span>
      <span className="ml-auto flex items-center gap-1 text-[10px] text-muted-foreground">
        <Loader2 className="h-3 w-3 animate-spin" />
        Reconnecting…
      </span>
    </div>
  );
}

/**
 * The actual call panel. Rendered either inline in the main document (corner card / fullscreen)
 * or, when popped out, into a Document Picture-in-Picture window via a portal.
 */
function CallPanel({
  isFullscreen,
  isPipped,
  onToggleFullscreen,
  onTogglePip,
  pipSupported,
  screenShareAudioMuted,
  setScreenShareAudioMuted,
}: {
  isFullscreen: boolean;
  isPipped: boolean;
  onToggleFullscreen: () => void;
  onTogglePip: () => void;
  pipSupported: boolean;
  screenShareAudioMuted: boolean;
  setScreenShareAudioMuted: (next: boolean | ((prev: boolean) => boolean)) => void;
}) {
  const {
    activeCall,
    remoteParticipants,
    reconnectingUserIds,
    localReconnecting,
    toggleMic,
    toggleCam,
    toggleScreenShare,
    screenShareActiveInRoom,
    captureScreenSnapshot,
    leaveCall,
    endCallForAll,
    switchMicrophone,
    switchCamera,
    switchSpeaker,
    setCallProject,
    patchTranscription,
    transcriptionPending,
  } = useCall();
  const { toast } = useToast();
  const [takingSnapshot, setTakingSnapshot] = useState(false);
  // Task #206 — poll the storage-status endpoint for every authenticated
  // participant so the camera button renders disabled with an
  // explanatory tooltip whenever the snapshot pipeline isn't usable.
  // Non-admins get a cheap configured-check only (the server skips the
  // R2/Dropbox network probes for non-admins); admins get the full
  // probe results that also feed the /admin/transcripts operator card.
  const { devices, hasLabels, speakerSupported, ensureLabels } = useMediaDevices(!!activeCall);
  const { user } = useAuth();
  const { data: snapshotStorageStatus } = useQuery<{
    r2: { configured: boolean; ok: boolean; error?: string };
    dropbox: { configured: boolean; ok: boolean; error?: string };
  } | null>({
    queryKey: ['/api/calls/_internal/snapshot-storage-status'],
    enabled: !!activeCall,
    staleTime: 60_000,
    refetchInterval: 60_000,
    retry: false,
  });
  const snapshotStorageBlocked =
    !!snapshotStorageStatus &&
    (!snapshotStorageStatus.r2.configured || !snapshotStorageStatus.r2.ok);
  const [minimized, setMinimized] = useState(false);
  const [, force] = useState(0);
  // Toggle only — polling lives inside CallConnectionStats.
  const [statsOpen, setStatsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [dragPos, setDragPos] = useState<{ x: number; y: number } | null>(() => {
    if (typeof window === 'undefined') return null;
    try {
      const raw = sessionStorage.getItem('callWindowPos');
      if (!raw) return null;
      const p = JSON.parse(raw);
      if (typeof p?.x === 'number' && typeof p?.y === 'number') return p;
    } catch {}
    return null;
  });
  const dragStateRef = useRef<{ pointerId: number; offsetX: number; offsetY: number } | null>(null);
  const [isDragging, setIsDragging] = useState(false);

  // Re-clamp the persisted position when the viewport shrinks so the card never
  // ends up off-screen.
  useEffect(() => {
    if (!dragPos) return;
    const onResize = () => {
      const el = containerRef.current;
      if (!el) return;
      const w = el.offsetWidth;
      const h = el.offsetHeight;
      const maxX = Math.max(0, window.innerWidth - w);
      const maxY = Math.max(0, window.innerHeight - h);
      setDragPos((p) => {
        if (!p) return p;
        const nx = Math.min(Math.max(0, p.x), maxX);
        const ny = Math.min(Math.max(0, p.y), maxY);
        return nx === p.x && ny === p.y ? p : { x: nx, y: ny };
      });
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [dragPos]);

  const onHeaderPointerDown = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    // Ignore drags initiated on interactive controls in the header.
    if ((e.target as HTMLElement).closest('button,a,input,select,textarea')) return;
    const el = containerRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    dragStateRef.current = {
      pointerId: e.pointerId,
      offsetX: e.clientX - rect.left,
      offsetY: e.clientY - rect.top,
    };
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
    setIsDragging(true);
    // Switch to absolute positioning at the current rect immediately so the
    // first move doesn't jump.
    setDragPos({ x: rect.left, y: rect.top });
    e.preventDefault();
  }, []);

  const onHeaderPointerMove = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    const s = dragStateRef.current;
    if (!s || s.pointerId !== e.pointerId) return;
    const el = containerRef.current;
    if (!el) return;
    const w = el.offsetWidth;
    const h = el.offsetHeight;
    const maxX = Math.max(0, window.innerWidth - w);
    const maxY = Math.max(0, window.innerHeight - h);
    const nx = Math.min(Math.max(0, e.clientX - s.offsetX), maxX);
    const ny = Math.min(Math.max(0, e.clientY - s.offsetY), maxY);
    setDragPos({ x: nx, y: ny });
  }, []);

  const onHeaderPointerUp = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    const s = dragStateRef.current;
    if (!s || s.pointerId !== e.pointerId) return;
    dragStateRef.current = null;
    try { (e.currentTarget as HTMLElement).releasePointerCapture(e.pointerId); } catch {}
    setIsDragging(false);
    const el = containerRef.current;
    if (!el) return;
    const w = el.offsetWidth;
    const h = el.offsetHeight;
    const margin = 16; // matches the original bottom-4/right-4 inset (1rem)
    const snapThreshold = 80;
    const rect = el.getBoundingClientRect();
    const nearLeft = rect.left < snapThreshold;
    const nearRight = window.innerWidth - rect.right < snapThreshold;
    const nearTop = rect.top < snapThreshold;
    const nearBottom = window.innerHeight - rect.bottom < snapThreshold;
    let next = { x: rect.left, y: rect.top };
    if (nearLeft || nearRight || nearTop || nearBottom) {
      const left = nearLeft || (!nearRight && rect.left + w / 2 < window.innerWidth / 2);
      const top = nearTop || (!nearBottom && rect.top + h / 2 < window.innerHeight / 2);
      next = {
        x: left ? margin : Math.max(0, window.innerWidth - w - margin),
        y: top ? margin : Math.max(0, window.innerHeight - h - margin),
      };
    }
    setDragPos(next);
    try { sessionStorage.setItem('callWindowPos', JSON.stringify(next)); } catch {}
  }, []);

  const { data: users } = useQuery<UserLite[]>({ queryKey: ['/api/users'], enabled: !!activeCall });
  const usersById = useMemo(() => {
    const map = new Map<number, UserLite>();
    (users ?? []).forEach((u) => map.set(u.id, u));
    return map;
  }, [users]);
  const lookupUser = useCallback(
    (identity: string | null | undefined) => {
      const id = userIdFromIdentity(identity);
      return id != null ? usersById.get(id) : undefined;
    },
    [usersById],
  );
  const selfUser = user ? usersById.get(user.id) ?? {
    id: user.id,
    firstName: user.firstName,
    lastName: user.lastName,
    shortCode: user.shortCode ?? undefined,
    username: user.username,
  } : undefined;

  // Compute who is currently inside the server's reconnect grace window but
  // already gone from the LiveKit room (LiveKit removes them on
  // ParticipantDisconnected, which fires the moment they drop). For those
  // users we render a ghost tile so the participant count and layout stay
  // stable across the reconnect.
  const reconnectingOnlyUserIds = useMemo(() => {
    if (reconnectingUserIds.size === 0) return [] as number[];
    const presentIds = new Set<number>();
    for (const p of remoteParticipants) {
      const id = userIdFromIdentity(p.identity);
      if (id != null) presentIds.add(id);
    }
    return Array.from(reconnectingUserIds).filter((uid) => !presentIds.has(uid));
  }, [reconnectingUserIds, remoteParticipants]);

  const isParticipantReconnecting = useCallback(
    (identity: string | null | undefined) => {
      const id = userIdFromIdentity(identity);
      return id != null && reconnectingUserIds.has(id);
    },
    [reconnectingUserIds],
  );

  // Re-render when any participant publishes/unpublishes a screen share so we can
  // flip into/out of speaker view.
  useEffect(() => {
    if (!activeCall) return;
    const room = activeCall.room;
    const trigger = () => force((n) => n + 1);
    room.on(RoomEvent.TrackPublished, trigger);
    room.on(RoomEvent.TrackUnpublished, trigger);
    room.on(RoomEvent.TrackSubscribed, trigger);
    room.on(RoomEvent.TrackUnsubscribed, trigger);
    room.on(RoomEvent.LocalTrackPublished, trigger);
    room.on(RoomEvent.LocalTrackUnpublished, trigger);
    return () => {
      room.off(RoomEvent.TrackPublished, trigger);
      room.off(RoomEvent.TrackUnpublished, trigger);
      room.off(RoomEvent.TrackSubscribed, trigger);
      room.off(RoomEvent.TrackUnsubscribed, trigger);
      room.off(RoomEvent.LocalTrackPublished, trigger);
      room.off(RoomEvent.LocalTrackUnpublished, trigger);
    };
  }, [activeCall]);

  const screenSharer = useMemo<null | { remote: RemoteParticipant | null; local: { isLocal: true } | null }>(() => {
    if (!activeCall) return null;
    // Prefer remote screen share so the local user sees what others are sharing.
    for (const p of remoteParticipants) {
      const has = Array.from(p.trackPublications.values()).some(
        (pub: TrackPublication) => pub.source === Track.Source.ScreenShare && !!pub.track,
      );
      if (has) return { remote: p, local: null };
    }
    // Derive local screen-share state from the actual track publication, not from
    // our toggleScreenShare flag — the user may stop sharing via the browser's
    // native "Stop sharing" UI without ever touching our button.
    const localPub = activeCall.room.localParticipant.getTrackPublication(Track.Source.ScreenShare);
    if (localPub?.track) return { remote: null, local: { isLocal: true } as const };
    return null;
  }, [activeCall, remoteParticipants]);

  // Reset the listener-side screen-share-audio mute whenever the sharer
  // changes or the share ends. Keyed on the remote participant identity (or
  // a stable "local"/"none" sentinel) so re-mounts that don't change owner
  // do not clear the user's choice.
  const screenSharerKey = screenSharer
    ? screenSharer.remote?.identity ?? 'local'
    : 'none';
  useEffect(() => {
    setScreenShareAudioMuted(false);
  }, [screenSharerKey]);

  const screenShareSupported = useMemo(() => {
    if (typeof navigator === 'undefined') return false;
    return !!navigator.mediaDevices?.getDisplayMedia;
  }, []);

  // ----- Project chip + transcription state (Phase B — #160) -----
  // Server is canonical: we read everything off `activeCall.session` (refreshed
  // on every `call:project_changed` / `call:transcription_state` /
  // `call:participant_consent` broadcast). Default to `null` / `idle` so we
  // never render undefined while the first broadcast is in flight.
  //
  // IMPORTANT: every hook below this banner MUST stay above the early-return
  // guards (`if (!activeCall) return null;` etc.) so React's hook order is
  // identical between the "no call" and "in call" renders. They all use
  // optional chaining so they're safe when activeCall is null.
  const callProject = activeCall?.session.project ?? null;
  const transcription = activeCall?.session.transcription ?? { status: 'idle' as const, projectId: null, segments: [] };

  const [projectSearch, setProjectSearch] = useState('');
  const [pickerExpanded, setPickerExpanded] = useState(false);
  // § 201 StGB: the recording mixes everyone in the room, so whoever arms
  // transcription confirms once that all participants agreed. The arm
  // request is parked here until the confirmation dialog is answered.
  const [consentDialogOpen, setConsentDialogOpen] = useState(false);
  const pendingArmRef = useRef<{ action: 'arm'; projectId: string } | null>(null);
  const consentConfirmRef = useRef<HTMLButtonElement | null>(null);

  // Recent-projects query for the picker. Only fires while the picker is
  // expanded so we don't pin a server query for every active call.
  const { data: recentProjects, isLoading: recentLoading } = useQuery<{ projects: RecentProjectLite[] }>({
    queryKey: ['/api/calls/recent-projects'],
    enabled: pickerExpanded,
    staleTime: 30_000,
  });

  // Task #232 — per-user "most recently transcribed in" list. Drives the
  // "Zuletzt transkribiert" shortcut row of the in-call project picker.
  // Cheap (LIMIT 6) — fetch eagerly so the shortcut is ready the moment the
  // user expands the picker.
  const { data: recentTranscribed } = useQuery<{ projects: Array<{ id: string; lastUsedAt: string }> }>({
    queryKey: ['/api/calls/recent-transcribed-projects'],
    staleTime: 60_000,
  });
  // Task #239 — relative-time hint ("vor 2 Std" / "gestern") next to each
  // "Zuletzt transkribiert" entry so users can disambiguate similar-looking
  // projects. `useNow` ticks once per minute, which is plenty for the
  // coarse-grained labels formatRelativeEn emits.
  const nowMs = useNow();
  const recentTranscribedLastUsedMap = useMemo(() => {
    const m = new Map<string, string>();
    for (const r of recentTranscribed?.projects ?? []) {
      m.set(r.id, r.lastUsedAt);
    }
    return m;
  }, [recentTranscribed]);

  // Task #232 — reuse the unified `['/api/projects']` cache for ALL thumbnails
  // (selected project header, "Zuletzt transkribiert" row, search results).
  // No new thumbnail-only endpoint and no per-project N+1 fetches.
  const { data: allProjects } = useQuery<Array<{ id: string; projectName: string; clientName?: string | null; thumbnail?: string | null }>>({
    queryKey: ['/api/projects'],
    staleTime: 30_000,
  });
  const projectThumbnailMap = useMemo(() => {
    const m = new Map<string, { thumbnail: string | null; projectName: string; clientName: string | null }>();
    for (const p of allProjects ?? []) {
      m.set(p.id, {
        thumbnail: p.thumbnail ?? null,
        projectName: p.projectName,
        clientName: p.clientName ?? null,
      });
    }
    return m;
  }, [allProjects]);

  // Task 172 Bug 2: server-confirmed access check for the project the call is
  // currently pinned to. We can't infer access from `recentProjects` alone
  // because that list only loads when the popover opens, and a colleague may
  // have started the call from a project this user has no access to (e.g.
  // private client work). Fires only when there is a callProject and resolves
  // to `{ access: boolean }` — falsy `access` triggers the "kein Zugriff"
  // chip variant + a disabled transcription toggle.
  const { data: projectAccess } = useQuery<{ projectId: string; access: boolean }>({
    queryKey: ['/api/calls/project-access', callProject?.id],
    enabled: !!callProject?.id,
    staleTime: 60_000,
  });
  const callerHasProjectAccess = !callProject?.id || projectAccess?.access !== false;
  const filteredProjects = useMemo(() => {
    const list = recentProjects?.projects ?? [];
    const q = projectSearch.trim().toLowerCase();
    if (!q) return list;
    return list.filter((p) =>
      p.projectName.toLowerCase().includes(q) ||
      p.id.toLowerCase().includes(q) ||
      (p.clientName ?? '').toLowerCase().includes(q),
    );
  }, [recentProjects, projectSearch]);

  const handlePickProject = useCallback(async (projectId: string | null) => {
    setProjectSearch('');
    await setCallProject(projectId);
  }, [setCallProject]);

  // Task 202: On Chrome/Mac the synthetic `click` on a picker item is
  // intermittently lost — a trackpad tap routinely emits
  // `pointerdown → pointermove (1–3px) → pointerup`, and the combination of
  // Radix's DismissableLayer (wraps PopoverContent) + Chrome's force-touch
  // trackpad event sequence can swallow the resulting click before it
  // reaches the button's onClick. iPad uses touch events with a wider
  // tap-slop tolerance and isn't affected, which is why #187's earlier
  // "drag surface swallows it" hypothesis didn't explain the asymmetry.
  //
  // The fix: bind the action to `pointerup` directly (the LAST event
  // guaranteed to fire on every tap/click, on every device) and keep
  // `click` ONLY as the keyboard fallback (Enter/Space produce a click
  // with `event.detail === 0`). A short dedupe window prevents the
  // belt-and-braces fallback from double-firing when both events arrive.
  const lastPickAtRef = useRef<{ id: string | null; at: number } | null>(null);
  const firePickProject = useCallback((projectId: string | null) => {
    const now = Date.now();
    const last = lastPickAtRef.current;
    if (last && last.id === projectId && now - last.at < 600) return;
    lastPickAtRef.current = { id: projectId, at: now };
    void handlePickProject(projectId);
  }, [handlePickProject]);

  // Transcription toggle. Source-of-truth flow:
  //   idle  → arm (requires project + consent)
  //   armed → pause
  //   paused → resume (requires consent if not yet given)
  // The Stop affordance lives inside the popover next to the active-segment
  // line so the toggle button itself stays a single-purpose play/pause.
  const handleToggleTranscription = useCallback(async () => {
    // Task #317: guard the whole handler — a second click while the first
    // is still resolving must be a no-op, otherwise it reads stale status
    // and fires a duplicate arm.
    if (toggleInFlightRef.current) return;
    toggleInFlightRef.current = true;
    try {
      switch (transcription.status) {
        case 'idle': {
          const projectId = callProject?.id ?? null;
          if (!projectId) { setPickerExpanded(true); return; }
          pendingArmRef.current = { action: 'arm', projectId };
          setConsentDialogOpen(true);
          return;
        }
        case 'armed':
          await patchTranscription({ action: 'pause' });
          return;
        case 'paused':
          await patchTranscription({ action: 'resume' });
          return;
      }
    } finally {
      toggleInFlightRef.current = false;
    }
  }, [transcription.status, callProject?.id, patchTranscription]);

  // Task #317: synchronous re-entrancy guard for handleToggleTranscription.
  const toggleInFlightRef = useRef(false);

  const handleConsentConfirm = useCallback(async () => {
    setConsentDialogOpen(false);
    const pending = pendingArmRef.current;
    pendingArmRef.current = null;
    if (!pending) return;
    await patchTranscription({ ...pending, allParticipantsAgreed: true });
  }, [patchTranscription]);

  const handleConsentCancel = useCallback(() => {
    setConsentDialogOpen(false);
    pendingArmRef.current = null;
  }, []);

  const handleStopTranscription = useCallback(async () => {
    await patchTranscription({ action: 'stop' });
  }, [patchTranscription]);

  if (!activeCall) return null;

  const isInitiator = user?.id === activeCall.session.initiatorUserId;
  const isVideo = activeCall.session.type === 'video';

  const totalInCall = remoteParticipants.length + reconnectingOnlyUserIds.length + 1;

  const presentation = resolveCallWindowPresentation({
    minimized,
    fullscreen: isFullscreen,
    pipped: isPipped,
  });

  // Minimized pill — only available in the regular windowed mode.
  if (presentation.mode === 'minimized') {
    return (
      <div
        className="fixed bottom-4 right-4 z-[9000] bg-card border border-border rounded-full shadow-xl px-3 py-2 flex items-center gap-2 pointer-events-auto"
        data-call-focus-surface="true"
      >
        <span className="text-xs font-medium">
          {isVideo ? 'Video' : 'Audio'} call · {totalInCall}
        </span>
        <Button size="sm" variant="ghost" className="h-7 px-2" onClick={() => setMinimized(false)}>Open</Button>
        <Button size="sm" variant="destructive" className="h-7 px-2" onClick={() => { void leaveCall(); }}>
          <PhoneOff className="h-3 w-3" />
        </Button>
      </div>
    );
  }

  // Container chrome differs by mode. PiP and fullscreen fill their host; windowed is a corner card.
  const windowed = presentation.mode === 'windowed';
  const containerClass = presentation.mode === 'pip'
    ? 'fixed inset-0 bg-card flex flex-col overflow-hidden'
    : presentation.mode === 'fullscreen'
      ? 'fixed inset-0 z-[9000] bg-background flex flex-col overflow-hidden'
      : cn(
          'fixed z-[9000] bg-card border border-border rounded-xl shadow-2xl w-[440px] max-w-[calc(100vw-2rem)] flex flex-col overflow-hidden pointer-events-auto',
          !dragPos && 'bottom-4 right-4',
          isDragging && 'select-none',
        );

  const containerStyle = windowed && dragPos ? { left: dragPos.x, top: dragPos.y } : undefined;

  const stageHeightClass = isFullscreen || isPipped ? 'flex-1 min-h-0' : 'h-[260px]';

  // Task #232 — breathing border on the call window while transcription is
  // armed. CSS keyframe in `client/src/index.css` honours
  // `prefers-reduced-motion` by swapping the animation for a static ring.
  const containerClassWithPulse = cn(
    containerClass,
    // Spec: only highlight when transcription is actively armed AND bound
    // to a concrete project (i.e. an open segment exists). Without the
    // projectId guard a transient `armed`-without-segment state (e.g.
    // mid-switch) would briefly flash the highlight.
    transcription.status === 'armed' && !!transcription.projectId && 'call-window-armed',
  );

  // Resolve display data for the currently-pinned project from the unified
  // `/api/projects` cache. Falls back to `callProject` (the in-call summary)
  // when the cache hasn't loaded yet so the panel never renders empty.
  const selectedProjectMeta = callProject?.id ? projectThumbnailMap.get(callProject.id) : undefined;
  const selectedProjectThumbnail = selectedProjectMeta?.thumbnail ?? null;
  const selectedProjectName = selectedProjectMeta?.projectName ?? callProject?.name ?? null;

  return (
    <div
      ref={containerRef}
      className={containerClassWithPulse}
      style={containerStyle}
      data-testid="in-call-window"
      data-call-focus-surface="true"
    >
      <div
        className={cn(
          'flex items-center justify-between px-3 py-2 border-b border-border bg-muted/40 shrink-0',
          windowed && (isDragging ? 'cursor-grabbing' : 'cursor-grab'),
        )}
        onPointerDown={windowed ? onHeaderPointerDown : undefined}
        onPointerMove={windowed ? onHeaderPointerMove : undefined}
        onPointerUp={windowed ? onHeaderPointerUp : undefined}
        onPointerCancel={windowed ? onHeaderPointerUp : undefined}
      >
        <div className="text-xs font-semibold flex items-center gap-2 min-w-0 flex-1">
          {isVideo ? <Video className="h-3.5 w-3.5 shrink-0" /> : <Mic className="h-3.5 w-3.5 shrink-0" />}
          <span className="shrink-0">{isVideo ? 'Video' : 'Audio'}</span>
          <span className="text-muted-foreground font-normal shrink-0">· {totalInCall}</span>
          {transcription.status === 'armed' && (
            <span
              className="ml-1 inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium bg-amber-100 text-amber-900 dark:bg-amber-900/40 dark:text-amber-100"
              title="Transcription in progress"
            >
              <Captions className="h-3 w-3" /> REC
            </span>
          )}
        </div>
        <div className="flex items-center gap-1">
          {!isPipped && (
            <button
              className={cn(
                'p-1 hover:bg-accent rounded',
                statsOpen && 'bg-accent text-accent-foreground',
              )}
              onClick={() => setStatsOpen((s) => !s)}
              title={statsOpen ? 'Hide connection stats' : 'Show connection stats'}
              data-testid="button-toggle-call-stats"
            >
              <Activity className="h-3.5 w-3.5" />
            </button>
          )}
          {isVideo && !isPipped && (
            <button
              className="p-1 hover:bg-accent rounded"
              onClick={onToggleFullscreen}
              title={isFullscreen ? 'Exit fullscreen' : 'Fullscreen'}
            >
              {isFullscreen ? <Minimize2 className="h-3.5 w-3.5" /> : <Maximize2 className="h-3.5 w-3.5" />}
            </button>
          )}
          {isVideo && pipSupported && !isFullscreen && (
            <button
              className="p-1 hover:bg-accent rounded"
              onClick={onTogglePip}
              title={isPipped ? 'Return to main window' : 'Pop out (always on top)'}
            >
              <PictureInPicture2 className="h-3.5 w-3.5" />
            </button>
          )}
          {!isFullscreen && !isPipped && (
            <button className="p-1 hover:bg-accent rounded" onClick={() => setMinimized(true)} title="Minimize">
              <X className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
      </div>

      {/* Task #232 — Transcription panel. Prominent control surface for the
          call's project assignment + transcription lifecycle. Replaces the
          cramped chip + tiny play/pause button that used to live in the
          header. Layout:
              [thumbnail] [ID · name + status pill] [primary action]
              [▼ Projekt wechseln expander]
                  ↳ Zuletzt transkribiert (max 6, thumbnails)
                  ↳ Suche + Trefferliste (thumbnails inline) */}
      <div className="border-b border-border bg-muted/20 shrink-0">
        <div className="flex items-center gap-2 px-3 py-2">
          {/* Selected-project chip with thumbnail */}
          <div className="flex items-center gap-2 min-w-0 flex-1">
            {callProject ? (
              selectedProjectThumbnail ? (
                <img
                  src={selectedProjectThumbnail}
                  alt=""
                  loading="lazy"
                  decoding="async"
                  className="h-8 w-auto max-w-[56px] rounded-sm object-cover shrink-0 bg-gray-800 ring-1 ring-white/10"
                />
              ) : (
                <FolderKanban className="h-8 w-8 p-1.5 shrink-0 rounded-sm bg-muted text-muted-foreground" />
              )
            ) : (
              <FolderX className="h-8 w-8 p-1.5 shrink-0 rounded-sm bg-muted text-muted-foreground" />
            )}
            <div className="min-w-0 flex-1">
              <div className="text-xs font-semibold truncate">
                {callProject ? (
                  <>
                    {callProject.id}
                    {selectedProjectName && <span className="font-normal text-muted-foreground"> · {selectedProjectName}</span>}
                  </>
                ) : (
                  <span className="text-muted-foreground">No project assigned</span>
                )}
              </div>
              <div className="flex items-center gap-1.5 mt-0.5">
                <span
                  className={cn(
                    'inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium',
                    transcription.status === 'armed'
                      ? 'bg-amber-100 text-amber-900 dark:bg-amber-900/40 dark:text-amber-100'
                      : transcription.status === 'paused'
                        ? 'bg-muted text-muted-foreground'
                        : 'bg-muted/60 text-muted-foreground',
                  )}
                  data-testid="pill-call-transcription-status"
                >
                  {transcription.status === 'armed' ? (
                    <><Captions className="h-3 w-3" /> Transcription active</>
                  ) : transcription.status === 'paused' ? (
                    <><Pause className="h-3 w-3" /> Paused</>
                  ) : (
                    <><CaptionsOff className="h-3 w-3" /> Ready</>
                  )}
                </span>
                {callProject && !callerHasProjectAccess && (
                  <span className="text-[10px] text-amber-700 dark:text-amber-400 font-medium">
                    No access
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* Primary transcription action */}
          <div className="flex items-center gap-1 shrink-0">
            {transcription.status !== 'idle' && (
              <Button
                size="sm"
                variant="ghost"
                className="h-8 px-2 text-[11px]"
                onClick={() => { void handleStopTranscription(); }}
                disabled={!callerHasProjectAccess || transcriptionPending}
                data-testid="button-call-transcription-stop"
                title="Stop transcription"
              >
                Stop
              </Button>
            )}
            <Button
              size="sm"
              variant={transcription.status === 'armed' ? 'secondary' : 'default'}
              onClick={() => { void handleToggleTranscription(); }}
              disabled={!callerHasProjectAccess || transcriptionPending}
              className="h-8 gap-1.5"
              data-testid="button-call-transcription-toggle"
              title={
                !callerHasProjectAccess
                  ? 'You do not have access to the project – transcription not available.'
                  : transcriptionPending ? 'Updating…'
                    : transcription.status === 'idle' ? 'Start transcription'
                      : transcription.status === 'armed' ? 'Pause'
                        : 'Resume'
              }
            >
              {transcriptionPending ? (
                <><Loader2 className="h-3.5 w-3.5 animate-spin" /> …</>
              ) : transcription.status === 'armed' ? (
                <><Pause className="h-3.5 w-3.5" /> Pause</>
              ) : transcription.status === 'paused' ? (
                <><Play className="h-3.5 w-3.5" /> Resume</>
              ) : (
                <><Captions className="h-3.5 w-3.5" /> Transcribe</>
              )}
            </Button>
          </div>
        </div>

        {/* Picker expander toggle */}
        <button
          type="button"
          onClick={() => setPickerExpanded((v) => !v)}
          className="w-full flex items-center justify-center gap-1 px-3 py-1 text-[11px] text-muted-foreground hover:bg-accent border-t border-border/60"
          data-testid="button-call-project-picker-expand"
        >
          {pickerExpanded ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
          {callProject ? 'Switch project' : 'Select project'}
        </button>

        {pickerExpanded && (
          <div className="border-t border-border/60 px-2 py-2 max-h-72 overflow-y-auto" data-testid="panel-call-project-picker">
            {/* Shortcut row — prefers the per-user "Zuletzt transkribiert"
                list (from /api/calls/recent-transcribed-projects). When the
                user has never transcribed anything yet, fall back to a
                generic "Empfohlene Projekte" section sourced from the
                existing /api/calls/recent-projects feed so the user
                always has a one-click shortcut row to start from. */}
            {(() => {
              const transcribedIds = (recentTranscribed?.projects ?? [])
                .map((r) => r.id)
                .filter((id) => id !== callProject?.id)
                .slice(0, 6);
              const useTranscribed = transcribedIds.length > 0;
              const fallbackIds = useTranscribed
                ? []
                : (recentProjects?.projects ?? [])
                    .map((p) => p.id)
                    .filter((id) => id !== callProject?.id)
                    .slice(0, 6);
              const ids = useTranscribed ? transcribedIds : fallbackIds;
              if (ids.length === 0) return null;
              const heading = useTranscribed ? 'Last transcribed' : 'Recently used';
              const testidPrefix = useTranscribed
                ? 'button-call-recent-transcribed'
                : 'button-call-recent-fallback';
              return (
                <div className="mb-2">
                  <div className="flex items-center gap-1 px-1 pb-1 text-[10px] uppercase tracking-wide text-muted-foreground font-semibold">
                    <History className="h-3 w-3" /> {heading}
                  </div>
                  <div className="flex flex-col gap-0.5">
                    {ids.map((projectId) => {
                      const meta =
                        projectThumbnailMap.get(projectId) ??
                        // Fallback when /api/projects hasn't loaded yet —
                        // synthesize a minimal meta from /recent-projects so
                        // the row still renders with name + ID.
                        (() => {
                          const rp = (recentProjects?.projects ?? []).find((p) => p.id === projectId);
                          return rp
                            ? { thumbnail: null, projectName: rp.projectName, clientName: rp.clientName }
                            : null;
                        })();
                      if (!meta) return null;
                      const relativeHint = useTranscribed
                        ? formatRelativeEn(recentTranscribedLastUsedMap.get(projectId), nowMs)
                        : null;
                      return (
                        <button
                          key={projectId}
                          type="button"
                          onPointerUp={(e) => { if (e.button === 0) firePickProject(projectId); }}
                          onClick={(e) => { if (e.detail === 0) firePickProject(projectId); }}
                          className="w-full flex items-center gap-2 px-1.5 py-1 text-xs rounded hover:bg-accent text-left"
                          data-testid={`${testidPrefix}-${projectId}`}
                        >
                          {meta.thumbnail ? (
                            <img
                              src={meta.thumbnail}
                              alt=""
                              loading="lazy"
                              decoding="async"
                              className="h-7 w-auto max-w-[48px] rounded-sm object-cover shrink-0 bg-gray-800 ring-1 ring-white/10"
                            />
                          ) : (
                            <FolderKanban className="h-7 w-7 p-1 shrink-0 rounded-sm bg-muted text-muted-foreground" />
                          )}
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-1.5">
                              <div className="truncate min-w-0 flex-1"><span className="font-medium">{projectId}</span> · {meta.projectName}</div>
                              {relativeHint && (
                                <span
                                  className="text-[10px] text-muted-foreground shrink-0 tabular-nums"
                                  data-testid={`text-call-recent-transcribed-relative-${projectId}`}
                                >
                                  {relativeHint}
                                </span>
                              )}
                            </div>
                            {meta.clientName && (
                              <div className="text-[10px] text-muted-foreground truncate">{meta.clientName}</div>
                            )}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>
              );
            })()}

            {/* Search input */}
            <div className="flex items-center gap-1 px-1 pb-1 border-b border-border/60">
              <Search className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
              <Input
                value={projectSearch}
                onChange={(e) => setProjectSearch(e.target.value)}
                placeholder="Search project…"
                className="h-7 text-xs border-0 shadow-none focus-visible:ring-0 px-1"
                data-testid="input-call-project-search-panel"
              />
            </div>

            {/* Clear current project */}
            {callProject && (
              <button
                type="button"
                onPointerUp={(e) => { if (e.button === 0) firePickProject(null); }}
                onClick={(e) => { if (e.detail === 0) firePickProject(null); }}
                className="w-full flex items-center gap-2 px-1.5 py-1 mt-1 text-xs hover:bg-accent rounded text-muted-foreground"
                data-testid="button-call-project-clear-panel"
              >
                <FolderX className="h-3.5 w-3.5" />
                Remove project
              </button>
            )}

            {/* Search results — thumbnails inline (ChatDock pattern) */}
            <div className="mt-1">
              {recentLoading ? (
                <div className="px-2 py-3 text-xs text-muted-foreground text-center">
                  <Loader2 className="h-3.5 w-3.5 animate-spin inline mr-1" />
                  Loading…
                </div>
              ) : filteredProjects.length === 0 ? (
                <div className="px-2 py-3 text-xs text-muted-foreground text-center">
                  No projects found.
                </div>
              ) : (
                filteredProjects.map((p) => {
                  const isCurrent = callProject?.id === p.id;
                  const meta = projectThumbnailMap.get(p.id);
                  return (
                    <button
                      key={p.id}
                      type="button"
                      onPointerUp={(e) => { if (e.button === 0) firePickProject(p.id); }}
                      onClick={(e) => { if (e.detail === 0) firePickProject(p.id); }}
                      className={cn(
                        'w-full flex items-center gap-2 px-1.5 py-1 text-xs rounded hover:bg-accent text-left',
                        isCurrent && 'bg-accent',
                      )}
                      data-testid={`button-call-project-option-panel-${p.id}`}
                    >
                      {meta?.thumbnail ? (
                        <img
                          src={meta.thumbnail}
                          alt=""
                          loading="lazy"
                          decoding="async"
                          className="h-7 w-auto max-w-[48px] rounded-sm object-cover shrink-0 bg-gray-800 ring-1 ring-white/10"
                        />
                      ) : (
                        <FolderKanban className="h-7 w-7 p-1 shrink-0 rounded-sm bg-muted text-muted-foreground" />
                      )}
                      <div className="min-w-0 flex-1">
                        <div className="font-medium truncate">{p.id} · {p.projectName}</div>
                        {p.clientName && (
                          <div className="text-[10px] text-muted-foreground truncate">{p.clientName}</div>
                        )}
                      </div>
                    </button>
                  );
                })
              )}
            </div>
          </div>
        )}
      </div>

      {/* Diagnostics panel — absolutely positioned inside the call container so
          it overlays the stage but never moves the stage layout. The panel's
          internal poll loop only runs while it's mounted. */}
      {statsOpen && activeCall && (
        <CallConnectionStats
          room={activeCall.room}
          remoteParticipants={remoteParticipants}
          identityToLabel={(identity) => {
            const u = lookupUser(identity);
            const p = remoteParticipants.find((rp) => rp.identity === identity) ?? null;
            return resolveDisplayName(u, p, identityFallbackLabel(identity));
          }}
          onClose={() => setStatsOpen(false)}
        />
      )}

      {/* Phase C (#161) cost-cap banner — takes precedence over the normal
          armed/paused banner. Tells every participant transcription is
          unavailable for the rest of the day. */}
      {activeCall?.session.costCapExceededAt ? (
        <div
          className="flex items-center justify-center gap-1.5 px-3 py-1 text-[11px] font-medium border-b shrink-0 text-rose-900 bg-rose-100 border-rose-300 dark:text-rose-100 dark:bg-rose-900/40 dark:border-rose-800"
          data-testid="banner-call-transcription-cost-cap"
        >
          <CaptionsOff className="h-3 w-3" />
          {`Transcription paused for today – daily cost limit reached.`}
        </div>
      ) : transcription.status !== 'idle' && (transcription.projectId || callProject?.id) && (
        /* Phase C (#161) status banner — transcription is now actually
           recording audio for the indicated project (Whisper batch
           transcription). Renders armed (active) vs paused. */
        <div
          className={cn(
            'flex items-center justify-center gap-1.5 px-3 py-1 text-[11px] font-medium border-b shrink-0',
            transcription.status === 'armed'
              ? 'text-amber-900 bg-amber-100 border-amber-300 dark:text-amber-100 dark:bg-amber-900/40 dark:border-amber-800'
              : 'text-muted-foreground bg-muted border-border',
          )}
          data-testid="banner-call-transcription"
        >
          {transcription.status === 'armed' ? <Captions className="h-3 w-3" /> : <CaptionsOff className="h-3 w-3" />}
          {/* Only render when we have a concrete project id so the banner
              never reads "...für Projekt  ..." with an empty gap during the
              brief render between a remote `switchProject` clearing and the
              next summary broadcast arriving. */}
          {transcription.status === 'armed'
            ? `Audio for project ${transcription.projectId ?? callProject?.id} is being recorded and transcribed.`
            : `Transcription paused for project ${transcription.projectId ?? callProject?.id}.`}
        </div>
      )}

      {/* Local-side reconnect banner. Mirrors the per-peer "Reconnecting…"
          treatment so the local user gets feedback while LiveKit silently
          retries our own connection. */}
      {localReconnecting && (
        <div
          className="flex items-center justify-center gap-1.5 px-3 py-1.5 text-[11px] font-medium text-white bg-amber-600/90 border-b border-amber-700 shrink-0"
          data-testid="local-reconnecting-banner"
        >
          <Loader2 className="h-3 w-3 animate-spin" />
          Reconnecting…
        </div>
      )}

      {/* Stage: speaker-view when someone is sharing, otherwise grid (video) or status text (audio). */}
      <div className={cn('p-3 flex flex-col gap-2 overflow-hidden', stageHeightClass)}>
        {isVideo ? (
          screenSharer ? (
            <>
              <div className="flex-1 min-h-0">
                <ScreenShareStage
                  remote={screenSharer.remote}
                  remoteUser={lookupUser(screenSharer.remote?.identity)}
                  local={screenSharer.local}
                  localUser={selfUser}
                  screenShareAudioMuted={screenShareAudioMuted}
                  onToggleScreenShareAudioMute={() => setScreenShareAudioMuted((m) => !m)}
                />
              </div>
              <div className="flex gap-2 overflow-x-auto shrink-0 pb-1">
                <LocalVideoPreview user={selfUser} compact />
                {remoteParticipants.map((p) => (
                  <ParticipantTile
                    key={p.sid}
                    participant={p}
                    user={lookupUser(p.identity)}
                    reconnecting={isParticipantReconnecting(p.identity)}
                    compact
                  />
                ))}
                {reconnectingOnlyUserIds.map((uid) => (
                  <ReconnectingPlaceholderTile
                    key={`reconnecting-${uid}`}
                    user={usersById.get(uid)}
                    fallbackLabel={`User ${uid}`}
                    compact
                  />
                ))}
              </div>
            </>
          ) : (
            <div
              className={cn(
                'flex-1 min-h-0 grid gap-2',
                remoteParticipants.length + reconnectingOnlyUserIds.length === 0 ? 'grid-cols-1' : 'grid-cols-2',
              )}
            >
              <LocalVideoPreview user={selfUser} />
              {remoteParticipants.map((p) => (
                <ParticipantTile
                  key={p.sid}
                  participant={p}
                  user={lookupUser(p.identity)}
                  reconnecting={isParticipantReconnecting(p.identity)}
                />
              ))}
              {reconnectingOnlyUserIds.map((uid) => (
                <ReconnectingPlaceholderTile
                  key={`reconnecting-${uid}`}
                  user={usersById.get(uid)}
                  fallbackLabel={`User ${uid}`}
                />
              ))}
            </div>
          )
        ) : (
          <div className="w-full flex-1 flex flex-col gap-2">
            <div className="text-xs text-muted-foreground text-center">
              {remoteParticipants.length + reconnectingOnlyUserIds.length === 0
                ? 'Waiting for others to join…'
                : `In call with ${remoteParticipants.length + reconnectingOnlyUserIds.length} other${remoteParticipants.length + reconnectingOnlyUserIds.length === 1 ? '' : 's'}`}
            </div>
            <div className="flex flex-col gap-1 overflow-y-auto">
              <div className="flex items-center gap-2 px-2 py-1 rounded-md">
                <Avatar user={selfUser} size="sm" />
                <span className="text-xs truncate">{formatUserDisplayName(selfUser, 'You')} (You)</span>
              </div>
              {remoteParticipants.map((p) => (
                <AudioParticipantRow
                  key={p.sid}
                  participant={p}
                  user={lookupUser(p.identity)}
                  reconnecting={isParticipantReconnecting(p.identity)}
                />
              ))}
              {reconnectingOnlyUserIds.map((uid) => (
                <AudioReconnectingPlaceholderRow
                  key={`reconnecting-${uid}`}
                  user={usersById.get(uid)}
                  fallbackLabel={`User ${uid}`}
                />
              ))}
            </div>
            {/* hidden audio sinks for remote participants */}
            <div className="hidden">
              {remoteParticipants.map((p) => (
                <ParticipantTile key={p.sid} participant={p} user={lookupUser(p.identity)} />
              ))}
            </div>
          </div>
        )}
      </div>

      <div className="flex items-center justify-center gap-2 p-3 border-t border-border bg-muted/30 shrink-0">
        {/* Mic toggle + device picker chevron, joined visually as one control. */}
        <div className="flex items-center">
          <Button
            size="icon"
            variant={activeCall.micEnabled ? 'secondary' : 'destructive'}
            onClick={() => { void toggleMic(); }}
            disabled={localReconnecting}
            className={!isPipped ? 'rounded-r-none' : undefined}
            title={
              localReconnecting
                ? 'Reconnecting… mic toggle is unavailable until reconnected'
                : activeCall.micEnabled ? 'Mute' : 'Unmute'
            }
            data-testid="button-toggle-mic"
          >
            {activeCall.micEnabled ? <Mic className="h-4 w-4" /> : <MicOff className="h-4 w-4" />}
          </Button>
          {!isPipped && (
            <DeviceMenu
              kind="audioinput"
              devices={devices.audioinput}
              currentId={activeCall.micDeviceId}
              onSelect={(id) => { void switchMicrophone(id); }}
              onEnsureLabels={() => { void ensureLabels(); }}
              disabled={localReconnecting}
              triggerLabel="Microphone"
              triggerVariant="chevron"
              hasLabels={hasLabels}
            />
          )}
        </div>
        {isVideo && (
          <>
            <div className="flex items-center">
              <Button
                size="icon"
                variant={activeCall.camEnabled ? 'secondary' : 'destructive'}
                onClick={() => { void toggleCam(); }}
                disabled={localReconnecting}
                className={!isPipped ? 'rounded-r-none' : undefined}
                title={
                  localReconnecting
                    ? 'Reconnecting… camera toggle is unavailable until reconnected'
                    : activeCall.camEnabled ? 'Stop camera' : 'Start camera'
                }
                data-testid="button-toggle-camera"
              >
                {activeCall.camEnabled ? <Video className="h-4 w-4" /> : <VideoOff className="h-4 w-4" />}
              </Button>
              {!isPipped && (
                <DeviceMenu
                  kind="videoinput"
                  devices={devices.videoinput}
                  currentId={activeCall.camDeviceId}
                  onSelect={(id) => { void switchCamera(id); }}
                  onEnsureLabels={() => { void ensureLabels(); }}
                  disabled={localReconnecting}
                  triggerLabel="Camera"
                  triggerVariant="chevron"
                  hasLabels={hasLabels}
                />
              )}
            </div>
            <Button
              size="icon"
              variant={activeCall.screenShareEnabled ? 'default' : 'secondary'}
              onClick={() => { void toggleScreenShare(); }}
              disabled={!screenShareSupported || localReconnecting}
              title={
                localReconnecting
                  ? 'Reconnecting… screen-share toggle is unavailable until reconnected'
                  : !screenShareSupported
                    ? 'Screen sharing is not available on this device. Join from a desktop browser to share your screen.'
                    : activeCall.screenShareEnabled ? 'Stop sharing' : 'Share screen'
              }
            >
              {activeCall.screenShareEnabled ? <MonitorOff className="h-4 w-4" /> : <Monitor className="h-4 w-4" />}
            </Button>
            {/* Task #206 — manual screenshot button. Enabled whenever SOMEONE
                in the room is sharing their screen (local or remote). One
                click = one frame captured and uploaded to staging; the
                pulse generator later promotes it into the Dropbox folder. */}
            <Button
              size="icon"
              variant="secondary"
              disabled={!screenShareActiveInRoom || localReconnecting || takingSnapshot || snapshotStorageBlocked}
              onClick={async () => {
                setTakingSnapshot(true);
                try {
                  await captureScreenSnapshot();
                  toast({ title: 'Screenshot saved' });
                } catch (e: any) {
                  toast({
                    title: 'Screenshot failed',
                    description: e?.message || 'Unknown error',
                    variant: 'destructive',
                  });
                } finally {
                  setTakingSnapshot(false);
                }
              }}
              title={
                snapshotStorageBlocked
                  ? 'Screenshot unavailable – storage not configured'
                  : !screenShareActiveInRoom
                    ? 'Start a screen share first'
                    : takingSnapshot
                      ? 'Taking screenshot…'
                      : 'Take screenshot now'
              }
              data-testid="button-take-screenshot"
            >
              <Camera className="h-4 w-4" />
            </Button>
          </>
        )}
        {/* Speaker selector — visible in both audio and video modes. On
            Safari/Firefox `setSinkId` is missing so the trigger is shown
            disabled with an explanatory tooltip rather than hidden. */}
        {!isPipped && (
          <DeviceMenu
            kind="audiooutput"
            devices={devices.audiooutput}
            currentId={activeCall.speakerDeviceId}
            onSelect={(id) => { void switchSpeaker(id); }}
            onEnsureLabels={() => { void ensureLabels(); }}
            disabled={localReconnecting}
            unsupported={!speakerSupported}
            unsupportedReason="Speaker selection isn't supported in this browser. Try Chrome, Edge, or Opera."
            triggerLabel="Speaker"
            triggerVariant="button"
            hasLabels={hasLabels}
          />
        )}
        <Button
          size="sm"
          variant="destructive"
          onClick={() => { void leaveCall(); }}
          className="gap-1"
        >
          <PhoneOff className="h-4 w-4" /> Leave
        </Button>
        {isInitiator && (remoteParticipants.length + reconnectingOnlyUserIds.length) > 0 && (
          <Button
            size="sm"
            variant="outline"
            onClick={() => { void endCallForAll(); }}
            className="gap-1"
            title="End call for everyone"
          >
            End all
          </Button>
        )}
      </div>

        {/* § 201 StGB confirmation before arming. The recording is one mixed
          track of everyone in the call, so the person starting it confirms
          that all participants agreed. Cancelling simply drops the request. */}
      <AlertDialog open={consentDialogOpen} onOpenChange={(open) => { if (!open) handleConsentCancel(); }}>
        {/* Bypass shadcn's AlertDialogContent so overlay + content sit in the
            reserved high-priority-dialog layer, above the call, notifications,
            and any portalled call menus. */}
        <AlertDialogPrimitive.Portal>
          <AlertDialogPrimitive.Overlay
            className="fixed inset-0 z-[11000] bg-black/80 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0"
          />
          <AlertDialogPrimitive.Content
            className="fixed left-[50%] top-[50%] z-[11001] grid w-full max-w-lg translate-x-[-50%] translate-y-[-50%] gap-4 border bg-background p-6 shadow-lg duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 sm:rounded-lg"
            data-testid="dialog-call-transcription-consent"
            onOpenAutoFocus={(event) => {
              event.preventDefault();
              consentConfirmRef.current?.focus();
            }}
          >
          <AlertDialogHeader>
            <AlertDialogTitle>Transkription starten (§ 201 StGB)</AlertDialogTitle>
            <AlertDialogDescription>
              Dieser Anruf wird als eine gemeinsame Tonspur aller Teilnehmenden
              aufgezeichnet und in 10-Minuten-Blöcken transkribiert. Nach
              deutschem Recht ist das nur zulässig, wenn alle Beteiligten
              ausdrücklich zugestimmt haben. Bitte bestätige, dass du alle im
              Anruf gefragt hast und alle einverstanden sind. Während der
              Aufzeichnung sehen alle den Hinweis „Transkription läuft“; du
              kannst jederzeit pausieren oder beenden.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              onClick={handleConsentCancel}
              data-testid="button-call-consent-cancel"
            >
              Abbrechen
            </AlertDialogCancel>
            <AlertDialogAction
              ref={consentConfirmRef}
              onClick={() => { void handleConsentConfirm(); }}
              data-testid="button-call-consent-confirm"
            >
              Alle haben zugestimmt – starten
            </AlertDialogAction>
          </AlertDialogFooter>
          </AlertDialogPrimitive.Content>
        </AlertDialogPrimitive.Portal>
      </AlertDialog>
    </div>
  );
}

/**
 * Outer host component — owns the fullscreen + Document PiP state and decides
 * whether to render the panel inline or portalled into the PiP window.
 *
 * The panel is non-blocking: it lives in a corner via fixed positioning and
 * never sits behind a full-viewport overlay or pointer-events trap, so the rest
 * of the app stays interactive while a call is active.
 */
export function InCallWindow() {
  const { activeCall } = useCall();
  const hostRef = useRef<HTMLDivElement | null>(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [pipWindow, setPipWindow] = useState<Window | null>(null);
  const pipSupported = typeof window !== 'undefined' && 'documentPictureInPicture' in window;

  // Track the browser fullscreen state so the icon stays accurate when the
  // user presses Esc.
  useEffect(() => {
    const onChange = () => setIsFullscreen(document.fullscreenElement === hostRef.current);
    document.addEventListener('fullscreenchange', onChange);
    return () => document.removeEventListener('fullscreenchange', onChange);
  }, []);

  const toggleFullscreen = useCallback(async () => {
    const el = hostRef.current;
    if (!el) return;
    try {
      if (document.fullscreenElement) {
        await document.exitFullscreen();
      } else {
        await el.requestFullscreen();
      }
    } catch (e) {
      console.warn('[Call] fullscreen toggle failed', e);
    }
  }, []);

  const togglePip = useCallback(async () => {
    if (!pipSupported) return;
    if (pipWindow) {
      try { pipWindow.close(); } catch {}
      setPipWindow(null);
      return;
    }
    try {
      // @ts-expect-error documentPictureInPicture is not in lib.dom yet
      const win: Window = await window.documentPictureInPicture.requestWindow({ width: 480, height: 360 });
      // Copy stylesheets so Tailwind classes render correctly in the PiP doc.
      Array.from(document.styleSheets).forEach((sheet) => {
        try {
          const rules = Array.from(sheet.cssRules as unknown as CSSRule[]).map((r) => r.cssText).join('');
          const style = win.document.createElement('style');
          style.textContent = rules;
          win.document.head.appendChild(style);
        } catch {
          // Cross-origin stylesheet — fall back to <link>
          if (sheet.href) {
            const link = win.document.createElement('link');
            link.rel = 'stylesheet';
            link.href = sheet.href;
            win.document.head.appendChild(link);
          }
        }
      });
      win.document.body.style.margin = '0';
      win.document.body.style.background = 'transparent';
      win.addEventListener('pagehide', () => setPipWindow(null));
      setPipWindow(win);
    } catch (e) {
      console.warn('[Call] PiP open failed', e);
    }
  }, [pipSupported, pipWindow]);

  // If the call ends, close any open PiP window.
  useEffect(() => {
    if (!activeCall && pipWindow) {
      try { pipWindow.close(); } catch {}
      setPipWindow(null);
    }
  }, [activeCall, pipWindow]);

  // Belt-and-braces: explicitly close the PiP window when the main tab is
  // going away. Browsers normally close PiP windows with their opener, but
  // covering this here means a paused-mid-teardown opener can't leave an
  // orphaned window. Use `pagehide` to mirror the main call cleanup path.
  useEffect(() => {
    if (!pipWindow) return;
    const handler = () => {
      try { pipWindow.close(); } catch {}
    };
    window.addEventListener('pagehide', handler);
    return () => window.removeEventListener('pagehide', handler);
  }, [pipWindow]);

  // Listener-side mute for the presenter's ScreenShareAudio. Lives at this
  // level so the same state can be (a) read by `GlobalCallAudioSink` to apply
  // `audio.muted = true` on the screen-share audio element (the sink lives
  // in the stable outer wrapper so it survives PiP toggles), and (b) toggled
  // by the `ScreenShareStage` overlay button inside the panel. Reset to
  // `false` on share-owner change is handled inside CallPanel.
  const [screenShareAudioMuted, setScreenShareAudioMuted] = useState(false);

  if (!activeCall) return null;

  const panel = (
    <CallPanel
      isFullscreen={isFullscreen}
      isPipped={!!pipWindow}
      onToggleFullscreen={toggleFullscreen}
      onTogglePip={togglePip}
      pipSupported={pipSupported}
      screenShareAudioMuted={screenShareAudioMuted}
      setScreenShareAudioMuted={setScreenShareAudioMuted}
    />
  );

  // When popped out, render the panel into the PiP window's body via a portal so
  // the same React tree (and same LiveKit Room subscriptions) keeps running.
  // STABLE root: a single wrapper element type that does NOT change across
  // popout toggles. Switching the root between `<>` and `<div>` would unmount
  // every sibling — including the audio sink — silencing the call every time
  // the user pops in or out. The sink lives here so its <audio> elements stay
  // in the original document and keep the call-accept autoplay grant; only
  // the visual `panel` is conditionally portaled into the PiP document.
  return (
    <div
      ref={hostRef}
      className={cn(
        // When popped out, the host is invisible (the panel lives in the PiP
        // doc) but must remain in the DOM so the sink stays mounted. When
        // fullscreen, take over the viewport. Otherwise, render inline.
        pipWindow ? 'hidden' : isFullscreen ? 'fixed inset-0 z-[9000]' : 'contents',
      )}
    >
      <GlobalCallAudioSink room={activeCall.room} screenShareAudioMuted={screenShareAudioMuted} />
      {pipWindow ? createPortal(panel, pipWindow.document.body) : panel}
    </div>
  );
}
